import React, { useCallback, useEffect, useState, useMemo } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Trash2, Plus, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { useT, useLanguage } from "@/lib/LanguageContext";
import LanguageAwareField from "./LanguageAwareField";
// Relative on purpose (no "@" alias under Jest) — same reasoning as LanguageAwareField.
import useFieldErrors from "../hooks/useFieldErrors";
import { requiredField } from "../lib/validation";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { cn } from "../lib/utils";
import useAdminLoad, { payloadShapeError } from "../hooks/useAdminLoad";
import LoadFailure from "../components/ui/LoadFailure";
import {
  SITE_TEXT_OVERRIDES,
  SITE_TEXT_OVERRIDE_KEYS,
  SITE_IMAGE_SLOT_KEYS,
  SITE_REPEATERS,
  CONTENT_SECTIONS,
  isRepeaterKey,
  readRepeaterItems,
  buildRepeaterWrites,
  sampleTextFor,
  isHiddenRow,
} from "@/lib/siteContent";

// Editable keys — the canonical site-copy vocabulary (lib/siteContent.js), which is also
// what the public renderers resolve. Labels come from i18n content.preset.{key}.
const PRESET_KEYS = SITE_TEXT_OVERRIDE_KEYS;
// The rows whose behaviour a label alone cannot explain show `content.hint.<key>`.
const HINTED_KEYS = new Set(SITE_TEXT_OVERRIDES.filter((o) => o.hint).map((o) => o.key));

// The one field on this screen the practitioner types free-form. Mirrors
// server.py ContentBlockUpsert.key (min_length=1, max_length=200): a block's VALUES are
// legitimately empty (clearing an override is how you go back to the default copy), its
// KEY never is.
const customKeySchema = z.object({ customKey: requiredField(200) });

export default function ContentAdmin() {
  const t = useT("admin");
  const tv = useT("common");
  const { lang } = useLanguage();
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();
  const [blocks, setBlocks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [customKey, setCustomKey] = useState("");

  const byKey = useMemo(() => Object.fromEntries(blocks.map((b) => [b.key, b])), [blocks]);

  // Memoized (Set lookup instead of filter+some, which was O(n²) on every render).
  // A repeating section's rows belong to that section's editor: listing them here too would
  // give one row two editors, and the generic one knows nothing about indices.
  // Image-slot keys (`site.heroBg`, `site.logo`, …) are owned by SiteImagesAdmin — showing
  // their raw media URLs here is confusing and actionless for the practitioner.
  const customBlocks = useMemo(() => {
    const presetKeys = new Set(PRESET_KEYS);
    return blocks.filter(
      (b) =>
        !presetKeys.has(b.key) &&
        !isRepeaterKey(b.key) &&
        !SITE_IMAGE_SLOT_KEYS.has(b.key) &&
        !b.key.startsWith("site."),
    );
  }, [blocks]);

  // Stable identity: `useAdminLoad` uses it as its effect dependency, and the save /
  // delete handlers below share the very same reader to refresh the blocks.
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/content");
    if (!Array.isArray(data)) throw payloadShapeError();
    setBlocks(data);
  }, []);
  // BUG-051 — a failed read is NOT a site with no copy, and here that mattered most: with
  // `blocks` stuck at `[]`, every editor rendered EMPTY, and pressing Save on an empty
  // editor POSTs an empty override — overwriting the live text on the public site with
  // nothing. So the editors are withheld entirely until a read succeeds.
  const { loadError, reload } = useAdminLoad(load);

  const getBlock = (key) => byKey[key] || { key, value_fr: "", value_en: "" };

  /**
   * @returns {Promise<boolean>} whether the block really reached the server — the custom-key
   *   creator below only clears its input on a true, so a failed save leaves the key the
   *   practitioner typed in front of them to retry.
   */
  const save = async (key, value_fr, value_en) => {
    setSavingKey(key);
    try {
      await api.post("/admin/content", { key, value_fr, value_en });
    } catch (e) {
      toast.error(extractApiError(e, tv("errors.saveFailed"), tv));
      setSavingKey(null);
      return false;
    }
    // The write above already succeeded — a failed refresh is a STALE editor, not a
    // failed save, and returning false for it would make the custom-key creator keep an
    // input whose block now exists (and a second Create would be refused as a duplicate).
    try {
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("toast.reloadFailed"), tv));
    }
    setSavingKey(null);
    return true;
  };

  /**
   * Save one repeating section: the write plan comes from lib/siteContent.js (indices
   * rewritten, vacated keys deleted or cleared), and every write goes through the very
   * same endpoints as a single block — no bulk route, no new vocabulary.
   */
  const saveRepeater = async (repeater, items) => {
    setSavingKey(repeater.key);
    try {
      const { upserts, deletes } = buildRepeaterWrites({ repeater, items, byKey, lang });
      for (const payload of upserts) await api.post("/admin/content", payload);
      // Upserts first, deletes last: the two sets are disjoint by construction (a vacated
      // index is never rewritten), and this order makes an interrupted save leave a list
      // that is too LONG — visibly wrong and fixable — rather than one missing its text.
      for (const key of deletes) await api.delete(`/admin/content/${encodeURIComponent(key)}`);
    } catch (e) {
      // The list is written row by row, so a failure can leave the section HALF written —
      // all the more reason to say so. The editor deliberately keeps the practitioner's
      // items (no reload here): every upsert is idempotent, so pressing Save again is the
      // correct recovery, and swapping their work for a partial server state would not be.
      toast.error(extractApiError(e, tv("errors.saveFailed"), tv));
      setSavingKey(null);
      return;
    }
    setSavingKey(null);
    // Every write landed — a failed refresh is a stale editor, not a failed save.
    try {
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("toast.reloadFailed"), tv));
    }
  };

  const remove = async (key) => {
    if (!window.confirm(t("content.confirmDelete", { key }))) return;
    try {
      await api.delete(`/admin/content/${encodeURIComponent(key)}`);
    } catch (e) {
      toast.error(extractApiError(e, t("toast.deleteFailed"), tv));
      return;
    }
    try {
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("toast.reloadFailed"), tv));
    }
  };

  return (
    <div data-testid="admin-content">
      <h1 className="font-display text-4xl text-eh-ink">{t("content.title")}</h1>
      <p className="text-eh-muted mt-1">{t("content.subtitle")}</p>

      <LoadFailure
        message={loadError ? extractApiError(loadError, tv("errors.loadFailed"), tv) : ""}
        onRetry={reload}
        retryLabel={tv("actions.retry")}
        testId="admin-content-load-error"
        retryTestId="admin-content-retry"
        className="mt-8"
      />

      {/* Withheld while the read is failing: every editor below would render EMPTY, and
          Save on an empty editor POSTs an empty override — blanking the live site copy. */}
      {!loadError && (
        <div className="mt-8 space-y-10">
        {/* Grouped by section: preset text fields + their repeater, in the same order
            the visitor sees them on the public site.  The old layout listed ALL presets
            first and ALL repeaters at the bottom, which placed "My journey · Chapters"
            below "Contact" — the exact complaint Emily raised. */}
        {CONTENT_SECTIONS.map(({ section, keys, repeater }) => (
          <div key={section} data-testid={`content-section-${section}`}>
            <h2 className="font-display text-lg text-eh-sage-deep mb-4 border-b border-eh-border-soft pb-2">
              {t(`content.section.${section}`)}
            </h2>
            <div className="space-y-5">
              {keys.map((key) => {
                const b = getBlock(key);
                return (
                  <Row
                    key={key}
                    preset={{ key, label: t(`content.preset.${key}`), hint: HINTED_KEYS.has(key) ? t(`content.hint.${key}`) : "" }}
                    block={b}
                    saved={key in byKey}
                    onSave={save}
                    onDelete={remove}
                    saving={savingKey === key}
                    t={t}
                  />
                );
              })}
              {repeater && (
                <RepeaterSection
                  repeater={repeater}
                  byKey={byKey}
                  lang={lang}
                  onSave={saveRepeater}
                  saving={savingKey === repeater.key}
                  t={t}
                />
              )}
            </div>
          </div>
        ))}

        {/* Custom blocks: only truly custom keys created via the API or legacy wizard;
            image-slot keys are now filtered out (see customBlocks memo). */}
        {customBlocks.length > 0 && (
          <div data-testid="content-section-custom">
            <h2 className="font-display text-lg text-eh-sage-deep mb-4 border-b border-eh-border-soft pb-2">
              {t("content.customPrefix")}
            </h2>
            <div className="space-y-5">
              {customBlocks.map((b) => (
                <Row key={b.key} preset={{ key: b.key, label: `${t("content.customPrefix")} · ${b.key}` }} block={b} saved onSave={save} onDelete={remove} saving={savingKey === b.key} t={t} />
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 bg-eh-cream border border-eh-border-soft rounded-3xl p-6">
          <h3 className="font-display text-xl text-eh-ink">{t("content.addCustom")}</h3>
          <p className="text-xs text-eh-muted mt-1">{t("content.addCustomHint")}</p>
          <div className="mt-3 flex items-start gap-2 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="ex: footer.tagline"
                value={customKey}
                onChange={(e) => { setCustomKey(e.target.value); clearFieldError("customKey"); }}
                aria-invalid={fieldErrors.customKey ? true : undefined}
                className={cn(
                  "w-full bg-white border border-eh-border-soft rounded-xl px-4 py-2.5 text-sm",
                  fieldErrors.customKey && invalidInputClass,
                )}
                data-testid="content-custom-key"
              />
              <FieldError
                message={fieldErrors.customKey ? tv(fieldErrors.customKey) : ""}
                testId="content-custom-key-error"
              />
            </div>
            <button
              onClick={async () => {
                // Judged ON CLICK, never by disabling the button: a button that is dead
                // because a field is empty states no reason the practitioner can act on.
                if (validate(customKeySchema, { customKey })) { toast.error(tv("validation.fixHighlighted")); return; }
                setBusy(true);
                const ok = await save(customKey.trim(), "", "");
                if (ok) setCustomKey("");
                setBusy(false);
              }}
              disabled={busy}
              className="btn-primary text-sm"
              data-testid="content-custom-create"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {t("content.create")}
            </button>
          </div>
        </div>
        </div>
      )}
    </div>
  );
}

/**
 * Editor for ONE repeating section (Story chapters, Approach pillars).
 *
 * Items are edited locally — add / remove / reorder are pure list operations — and the
 * whole list is written at once on Save, because an index is storage, not identity:
 * removing item 1 renames item 2's rows, so saving "just that item" is not a thing.
 *
 * Local state is re-seeded from the server ONLY when the saved list actually changes
 * (value comparison, not object identity): reloading after a save must not wipe the edits
 * in progress in the OTHER section on the same page.
 */
const RepeaterSection = ({ repeater, byKey, lang, onSave, saving, t }) => {
  const savedJson = useMemo(
    () => JSON.stringify(readRepeaterItems(repeater, byKey, lang)),
    [repeater, byKey, lang],
  );
  const [items, setItems] = useState(() => JSON.parse(savedJson));
  useEffect(() => { setItems(JSON.parse(savedJson)); }, [savedJson]);

  const label = t(`content.repeater.${repeater.key}.label`);
  const blank = () => Object.fromEntries(repeater.fields.map((f) => [f.name, ""]));
  const setField = (index, name, value) =>
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, [name]: value } : it)));
  const move = (index, delta) =>
    setItems((prev) => {
      const next = [...prev];
      [next[index], next[index + delta]] = [next[index + delta], next[index]];
      return next;
    });

  return (
    <div data-testid={`repeater-${repeater.key}`} className="bg-eh-cream border border-eh-border-soft rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-eh-sage-deep">{label}</div>
          <div className="font-mono text-xs text-eh-muted mt-0.5">{repeater.key}</div>
          <div className="text-xs text-eh-muted mt-1">
            {t("content.repeater.langNote", { lang: String(lang || "").toUpperCase() })}
          </div>
        </div>
        <button
          onClick={() => onSave(repeater, items)}
          data-testid={`repeater-${repeater.key}-save`}
          className="btn-primary text-xs"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} {t("actions.save")}
        </button>
      </div>

      {items.length === 0 && (
        <p data-testid={`repeater-${repeater.key}-empty`} className="mt-3 text-sm text-eh-muted">
          {t(`content.repeater.${repeater.key}.empty`)}
        </p>
      )}

      <div className="mt-4 space-y-4">
        {items.map((item, index) => (
          <div
            key={index}
            data-testid={`repeater-${repeater.key}-item-${index}`}
            className="bg-white border border-eh-border-soft rounded-xl p-4"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs uppercase tracking-[0.18em] text-eh-sage-deep">
                {t(`content.repeater.${repeater.key}.item`, { n: index + 1 })}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  title={t("content.repeater.moveUp")}
                  data-testid={`repeater-${repeater.key}-up-${index}`}
                  className="p-2 rounded-full text-eh-muted hover:bg-eh-cream disabled:opacity-30"
                >
                  <ArrowUp size={12} />
                </button>
                <button
                  onClick={() => move(index, 1)}
                  disabled={index === items.length - 1}
                  title={t("content.repeater.moveDown")}
                  data-testid={`repeater-${repeater.key}-down-${index}`}
                  className="p-2 rounded-full text-eh-muted hover:bg-eh-cream disabled:opacity-30"
                >
                  <ArrowDown size={12} />
                </button>
                <button
                  onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                  title={t("content.repeater.remove")}
                  data-testid={`repeater-${repeater.key}-remove-${index}`}
                  className="p-2 rounded-full text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
            <div className="mt-3 space-y-2">
              {repeater.fields.map((field) => {
                const shared = {
                  value: item[field.name] || "",
                  onChange: (e) => setField(index, field.name, e.target.value),
                  placeholder: t(`content.repeater.field.${field.name}`),
                  "data-testid": `repeater-${repeater.key}-${index}-${field.name}`,
                  className:
                    "w-full bg-white border border-eh-border-soft rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-eh-sage-deep",
                };
                return field.multiline ? (
                  <textarea key={field.name} rows={3} {...shared} className={`${shared.className} resize-none`} />
                ) : (
                  <input key={field.name} type="text" {...shared} />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => setItems((prev) => [...prev, blank()])}
          disabled={items.length >= repeater.max}
          data-testid={`repeater-${repeater.key}-add`}
          className="btn-secondary text-xs disabled:opacity-40"
        >
          <Plus size={12} /> {t(`content.repeater.${repeater.key}.add`)}
        </button>
        <span className="text-xs text-eh-muted">{t("content.repeater.max", { max: repeater.max })}</span>
      </div>
    </div>
  );
};

/**
 * One text row. `saved` = a `content_blocks` row exists for the key, whatever its text:
 * delete is offered for every saved row, because for an `emptyHides` slot an EMPTY saved
 * row is what hides the line, and deleting it is the only way back to the sample.
 */
const Row = ({ preset, block, saved, onSave, onDelete, saving, t }) => {
  const [fr, setFr] = useState(block.value_fr || "");
  const [en, setEn] = useState(block.value_en || "");
  useEffect(() => { setFr(block.value_fr || ""); setEn(block.value_en || ""); }, [block.value_fr, block.value_en]);

  return (
    <div className="bg-eh-cream border border-eh-border-soft rounded-2xl p-5" data-testid={`content-row-${preset.key}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-eh-sage-deep font-semibold">{preset.label}</div>
          <div className="font-mono text-xs text-eh-muted mt-0.5">{preset.key}</div>
          {preset.hint && (
            <p className="text-xs text-eh-muted mt-1 max-w-xl" data-testid={`content-hint-${preset.key}`}>{preset.hint}</p>
          )}
          {saved && isHiddenRow(preset.key, block) && (
            <span
              className="inline-block mt-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900"
              data-testid={`content-hidden-${preset.key}`}
            >
              {t("content.hiddenOnSite")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onSave(preset.key, fr, en)} className="btn-primary text-xs" data-testid={`content-save-${preset.key}`}>
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} {t("actions.save")}
          </button>
          {saved && (
            <button onClick={() => onDelete(preset.key)} className="p-2 rounded-full text-red-600 hover:bg-red-50" data-testid={`content-delete-${preset.key}`}>
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>
      <LanguageAwareField
        valueFr={fr}
        valueEn={en}
        onChangeFr={setFr}
        onChangeEn={setEn}
        multiline
        rows={2}
        // An empty field shows, greyed, what the site displays for it: the sample.
        placeholderFr={sampleTextFor(preset.key, "fr")}
        placeholderEn={sampleTextFor(preset.key, "en")}
        testid={`content-field-${preset.key}`}
      />
    </div>
  );
};
